import { FC } from "react";
//#region types
import { AccumulatorsSummaryItem, WintSalaryReportTableProps } from "models/propTypes/wintSalaryPropTypes";
import { User } from "models/propTypes/userProfilePropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelWintSalaryReport as WintSalaryAPIModel } from "wint-api-client/dist/types";
import { Amount } from "models/utilTypes/currencyType";
import { CurrencyCode } from "models/enums/currency";
import { Hours } from "models/utilTypes/hourType";
import { HourEnums, WintSalaryEnums } from "models/enums";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { NestedTable, SimpleTooltip, Tags } from "components/common";
import WintDeviationRowFooter from "./wintDeviationRowFooter";
import NetOrBenefitIndicator from "./netOrBenefitIndicator";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const WintSalaryReportTable: FC<WintSalaryReportTableProps> = (props) => {
  const {
    hideUserPayoutDetails = false,
    allowCheckBoxSelection,
    onRowsChange,
    rows,
    reloadHandler,
    hiddenColumns,
    loading,
    allowGrouping,
    onRowClick,
    hidePagination,
    hideMoreActionsButton,
    expandFirstRowByDefault,
    disableUserLink,
    regularPayoutDate,
    unscheduledPayoutLabel,
  } = props;

  const formatStartAndEndDate = (row: WintSalaryAPIModel.WintSalaryReportRowColumn) => {
    if (row.startDate === undefined || row.endDate === undefined) {
      return "";
    }

    return DateHelper.formatDateRange(row.startDate, row.endDate, "-");
  };

  // The API serializes an unset month as JSON null, not as an absent key, so this has to reject both:
  // getDateFromYearMonth(null) yields 1899-12-01 rather than an empty cell.
  const formatSalaryMonth = (row: WintSalaryAPIModel.WintSalaryReportRowColumn) =>
    row.salaryYearMonth != null
      ? DateHelper.formatMonth(DateHelper.getDateFromYearMonth(row.salaryYearMonth))
      : "";

  // The row's own state only says whether the registration was reviewed. Whether its money has been
  // dealt with is the paying month's state, and since current-month placement the two can differ —
  // a reviewed deviation can still be pending on a later, unapproved month (DEV-28874).
  // Nullish check, not truthiness: Open is 0, and a row the service left unset arrives as null, which
  // must fall through to the old flag rather than read as settled.
  const isSalaryMonthSettled = (row?: WintSalaryAPIModel.WintSalaryReportRowColumn) =>
    row?.salaryMonthState != null
      ? row.salaryMonthState !== WintSalaryAPIModel.SalaryMonthState.Open
      : (row?.isAlreadyApproved ?? false);

  const rowFooter = (row: WintSalaryAPIModel.WintSalaryReportRow, rowIndex: number) => {
    const startDate = row?.columns?.[0]?.startDate ? new Date(row?.columns?.[0]?.startDate) : new Date();
    const endDate = row?.columns?.[0]?.endDate ? new Date(row?.columns?.[0]?.endDate) : undefined;

    return (
      <WintDeviationRowFooter
        person={{ id: Number(row?.personId), name: row?.description ?? "" }}
        startDate={startDate}
        endDate={endDate}
        regularPayoutAmount={row?.payoutDetails?.regularPayoutAmount ?? 0}
        taxAmount={row?.payoutDetails?.taxAmount ?? 0}
        earlyPayoutsAmount={row?.payoutDetails?.earlyPayoutsAmount}
        regularPayoutDate={regularPayoutDate}
        unscheduledPayoutLabel={unscheduledPayoutLabel}
        payouts={row?.payoutDetails?.payouts}
        taxTable={row?.payoutDetails?.taxTable ?? ""}
        accumulators={
          row?.accumulatorDetails?.items?.map(
            (item): AccumulatorsSummaryItem => ({
              description: item.description ?? "",
              change: item.change,
              total: item.total,
              children: item.children?.map((child) => ({
                description: child.description ?? "",
                change: child.change,
                total: child.total,
              })),
            })
          ) ?? []
        }
        hideUserPayoutDetails={hideUserPayoutDetails}
        reloadHandler={reloadHandler}
        hideMoreActions={hideMoreActionsButton ?? row?.columns?.[0]?.isAlreadyApproved ?? false}
        actionType={(allowGrouping ?? false) ? "moreOptions" : "add"}
      />
    );
  };

  const formatValueWithSign = (value: number) => {
    return LocalizationHelperInstance.getFormattedNumber(value, 0, 2, "exceptZero");
  };

  const formatAbsence = (row: WintSalaryAPIModel.WintSalaryReportRowColumn) => {
    if (row.absenceUnit === undefined) {
      return undefined;
    }

    switch (row.absenceUnit) {
      case WintSalaryAPIModel.AbsenceUnit.Days:
        return row.absenceDurationInDays !== undefined
          ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfDays, "", {
              num: `${row.absenceDurationInDays}`,
            })
          : undefined;
      case WintSalaryAPIModel.AbsenceUnit.Hours:
        return row.absenceDurationInHours !== undefined
          ? new Hours(row.absenceDurationInHours, HourEnums.HourCode.SHORT)
          : undefined;
      default:
        return undefined;
    }
  };

  const convertEventCategoryToIndicatorType = (category: WintSalaryAPIModel.WintSalaryEventCategory) => {
    switch (category) {
      case WintSalaryAPIModel.WintSalaryEventCategory.NetSalary:
        return WintSalaryEnums.NetOrBenefitIndicatorType.Net;
      case WintSalaryAPIModel.WintSalaryEventCategory.Benefits:
        return WintSalaryEnums.NetOrBenefitIndicatorType.Benefit;
      default:
        return WintSalaryEnums.NetOrBenefitIndicatorType.Net;
    }
  };

  const renderCurrencyAmount = (amount: number, showParentheses: boolean) => {
    const formattedAmount = LocalizationHelperInstance.getFormattedCurrency(amount, CurrencyCode.SEK, 0, 2);
    return showParentheses ? `(${formattedAmount})` : formattedAmount;
  };

  const formatAmount = (row: WintSalaryAPIModel.WintSalaryReportRowColumn) => {
    if (row.amount === undefined) {
      return undefined;
    }

    const amountUnit = row.amountUnit ?? WintSalaryAPIModel.AmountUnit.Currency;

    switch (amountUnit) {
      case WintSalaryAPIModel.AmountUnit.Days:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfDays, "", {
          num: `${row.amount}`,
        });
      case WintSalaryAPIModel.AmountUnit.Hours:
        return new Hours(row.amount, HourEnums.HourCode.SHORT);
      case WintSalaryAPIModel.AmountUnit.Currency:
        return (
          <Grid container item direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
            {row.salaryEventCategory !== undefined && (
              <Grid item>
                <NetOrBenefitIndicator type={convertEventCategoryToIndicatorType(row.salaryEventCategory)} />
              </Grid>
            )}
            <Grid item>{renderCurrencyAmount(row.amount ?? 0, row.salaryEventCategory != null)}</Grid>
          </Grid>
        );
      default:
        return undefined;
    }
  };

  return (
    <NestedTable<WintSalaryAPIModel.WintSalaryReportRow, WintSalaryAPIModel.WintSalaryReportRowColumn>
      className={cssExports.reportTableContainer}
      properties={{
        description: {
          description: TranslationKeys.Common.Name,
          formatter: (row: WintSalaryAPIModel.WintSalaryReportRow) => {
            if (row.rowLevel === WintSalaryAPIModel.WintSalaryReportRowLevel.Level1EmploymentSummary) {
              return new User(Number(row.personId), row.description, false, true, disableUserLink);
            }

            const column = row.columns?.[0];
            let descriptionText = row.description ?? "";
            if (column?.isCorrectionDeletion) {
              descriptionText += " (borttagen)";
            } else if (column?.isCorrected) {
              descriptionText += " (korrigerad)";
            }

            return (
              <SimpleTooltip text={descriptionText} showOnlyOnOverflow>
                <Grid item className={cssExports.truncateString}>
                  {descriptionText}
                </Grid>
              </SimpleTooltip>
            );
          },
          maxWidth: 125,
          className: cssExports.freezeCell,
        },
        group: allowGrouping
          ? {
              formatter: (row: WintSalaryAPIModel.WintSalaryReportRow) =>
                LocalizationHelperInstance.getFormattedMessage(
                  row.columns && row.columns[0].isAlreadyApproved
                    ? TranslationKeys.Common.Approved
                    : TranslationKeys.Common.ToApprove
                ),
            }
          : undefined,
      }}
      repeatProperties={{
        key: "columns",
        properties: {
          startDate: {
            description: TranslationKeys.Common.Date,
            formatter: formatStartAndEndDate,
          },
          salaryYearMonth: {
            description: TranslationKeys.SalaryDeviation.SalaryMonth,
            formatter: formatSalaryMonth,
          },
          workedHours: {
            description: TranslationKeys.SalaryDeviation.WorkedTime,
            formatter: (row: WintSalaryAPIModel.WintSalaryReportRowColumn) =>
              row.workedHours !== undefined
                ? new Hours(formatValueWithSign(row.workedHours), HourEnums.HourCode.SHORT)
                : undefined,
            alignment: "right",
          },
          absenceDurationInDays: {
            description: TranslationKeys.SalaryDeviation.Absence,
            formatter: formatAbsence,
            alignment: "right",
          },
          timeBalancesHours: {
            description: TranslationKeys.SalaryDeviation.TimeBalances,
            formatter: (row: WintSalaryAPIModel.WintSalaryReportRowColumn) =>
              row.timeBalancesHours !== undefined
                ? new Hours(formatValueWithSign(row.timeBalancesHours), HourEnums.HourCode.SHORT)
                : undefined,
            alignment: "right",
          },
          compensationsValue: {
            description: TranslationKeys.SalaryDeviation.Compensations,
            formatter: (row: WintSalaryAPIModel.WintSalaryReportRowColumn) =>
              row.compensationsValue !== undefined ? new Amount(CurrencyCode.SEK, row.compensationsValue) : null,
            alignment: "right",
          },
          amount: {
            description: TranslationKeys.Common.Amount,
            formatter: formatAmount,
            alignment: "right",
          },
          state: {
            description: TranslationKeys.Common.State,
            alignment: "right",
            formatter: (
              row: WintSalaryAPIModel.WintSalaryReportRowColumn,
              rowIndex?: number,
              depth?: number,
              repeatIndex?: number,
              parentRow?: any
            ) => {
              if (depth !== 2) {
                return undefined;
              }
              return (
                <Grid container item direction="row" alignItems="center" justifyContent="flex-end">
                  <Tags
                    tags={[
                      isSalaryMonthSettled(row)
                        ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Approved)
                        : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ToApprove),
                    ]}
                    className={isSalaryMonthSettled(row) ? cssExports.greenTag : cssExports.blueTag}
                  />
                </Grid>
              );
            },
          },
        },
      }}
      childrenKey="children"
      hasMultiLevels
      allowCheckBoxSelection={allowCheckBoxSelection}
      onRowSelectionsChange={onRowsChange}
      rows={rows ?? []}
      rowFooterRenderer={rowFooter}
      loading={loading}
      pagination={hidePagination ? undefined : "advanced"}
      maxRowCount={20}
      hiddenColumns={hiddenColumns}
      rowClickHandler={onRowClick}
      initialExpansionRowIndex={expandFirstRowByDefault ? 0 : undefined}
    />
  );
};

export default WintSalaryReportTable;
